import { useEffect, useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common/src/controllers/business-entity-context/index.tsx';
import { localStorage } from '@atlassian/jira-business-list/src/common/utils/storage.tsx';

const QUERY_FIELDS_ID_LOCAL_STORAGE_KEY = 'query_fields_id';

export const useFieldsCache = ({
	loading,
	error,
	getQueryFieldIds,
}: {
	loading: boolean;
	error?: Error;
	getQueryFieldIds: () => string[];
}) => {
	const { data: entity } = useBusinessEntity();

	const queryFieldsStorageKey = `${entity.id}_${QUERY_FIELDS_ID_LOCAL_STORAGE_KEY}`;
	const [queryFieldIds, setQueryFieldsIds] = useState(localStorage.get(queryFieldsStorageKey));

	// save queryFieldIds in localStorage on initial page load when queryFieldIds are empty in localStorage
	useEffect(() => {
		if (!loading && !error) {
			const newQueryFieldIds = getQueryFieldIds();
			if (!isEqual(queryFieldIds, newQueryFieldIds)) {
				setQueryFieldsIds(newQueryFieldIds);
				localStorage.set(queryFieldsStorageKey, newQueryFieldIds);
			}
		}
	}, [loading, error, getQueryFieldIds, queryFieldsStorageKey, queryFieldIds, setQueryFieldsIds]);

	const getQueryFieldIdsWithCache = useCallback(() => {
		// return from cache (localStorage)
		if (queryFieldIds && Array.isArray(queryFieldIds)) {
			return queryFieldIds;
		}
		// returns nothing if available fields query is in progress. But if cache is empty, it calls getQueryFieldIds
		if (loading) {
			return [];
		}
		return getQueryFieldIds();
	}, [getQueryFieldIds, loading, queryFieldIds]);

	return { getQueryFieldIdsWithCache };
};
